import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router";
import {
  flushPendingServerAnalyticsEvents,
  trackAnalyticsEvent,
  trackAnalyticsPageView,
} from "../analytics/analyticsClient";
import { isReaderRoute } from "../utils/isReaderRoute";

export function getAnalyticsTrackingKey(pathnameAndSearch: string): string {
  const [pathname] = pathnameAndSearch.split("?");
  if (!isReaderRoute(pathname)) {
    return pathnameAndSearch;
  }

  const [, readSegment, releaseKey] = pathname.split("/");
  if (readSegment !== "read" || !releaseKey) {
    return pathnameAndSearch;
  }

  return `/read/${releaseKey}`;
}

export function useGaTracking(trackLocation?: boolean) {
  const location = useLocation();
  const lastTrackedPath = useRef<string>();
  const currentPath = location.pathname + location.search;
  const trackingKey = getAnalyticsTrackingKey(currentPath);

  const trackGaPageView = useCallback(() => {
    if (!import.meta.env.PROD) {
      console.info("tracking.trackGaPageView", currentPath);
    }
    trackAnalyticsPageView(trackingKey);
  }, [currentPath, trackingKey]);

  useEffect(() => {
    flushPendingServerAnalyticsEvents();
  }, []);

  useEffect(() => {
    if (!trackLocation) return;

    if (lastTrackedPath.current === trackingKey) return;
    trackGaPageView();
    lastTrackedPath.current = trackingKey;
  }, [trackGaPageView, trackLocation, trackingKey]);

  const tracking = {
    trackGaPageView,
    removeThirdPartyGaId: (name: string) => {
      if (!import.meta.env.PROD) {
        console.info(
          "tracking.removeThirdPartyGaId (noop in Firebase-only tracking)",
          name,
        );
      }
    },
    addThirdPartyGaId: (name: string, gaId: string) => {
      if (!import.meta.env.PROD) {
        console.info(
          "tracking.addThirdPartyGaId (noop in Firebase-only tracking)",
          name,
          gaId,
        );
      }
    },
    trackGaEvent: (type: string, props: Record<string, unknown> = {}) => {
      trackAnalyticsEvent(type, props);
    },
  };

  return tracking;
}
